import { useRequest } from 'ahooks'
import { Message } from '@/components'
import { t } from '@/i18n'
import {
  getTemplateList,
  getTemplateCaseList,
  addTemplate,
  editTemplate,
  deleteTemplate,
  getTemplateDetail,
  GetTemplateListParams,
  AddTemplateParams,
  EditTemplateParams,
  getVerificationModeList,
  GetTemplateCaseListParams,
  getTemplateCaseIds,
  batchUnbindTemplateCase,
  copyTemplate,
  GetTemplateCaseIdsByFilterParams,
  getTemplateCaseIdsByFilter,
  getAttackTypeList,
  getAllAttackTypes,
} from '@/api/useCase-knowledge-base/tempate-center'

// 获取用例模板列表
export const useTemplateList = () => {
  const { runAsync: run, loading } = useRequest(
    (params: GetTemplateListParams) => getTemplateList(params),
    {
      manual: true,
      onError: (error: any) => {
        Message.error(error.msg || t('获取用例模板列表失败'))
      },
    },
  )

  return [run, loading] as const
}

// 获取用例模板详情
export const useTemplateDetail = () => {
  const { runAsync: run, data } = useRequest(
    (templateId: string) => getTemplateDetail(templateId),
    {
      manual: true,
      onError: (error: any) => {
        Message.error(error.msg || t('获取用例模板详情失败'))
      },
    },
  )

  return [run, data?.data] as const
}

// 添加用例模板
export const useAddTemplate = () => {
  const { runAsync: run, loading } = useRequest(
    (params: AddTemplateParams) => addTemplate(params),
    {
      manual: true,
      onError: (error: any) => {
        Message.error(error.msg || t('添加用例模板失败'))
      },
    },
  )

  return [run, loading] as const
}

// 编辑用例模板
export const useEditTemplate = () => {
  const { runAsync: run, loading } = useRequest(
    (params: EditTemplateParams) => editTemplate(params),
    {
      manual: true,
      onError: (error: any) => {
        Message.error(error.msg || t('编辑用例模板失败'))
      },
    },
  )

  return [run, loading] as const
}

// 删除用例模板
export const useDeleteTemplate = () => {
  const { runAsync: run, loading } = useRequest(
    (templateIds: string[]) => deleteTemplate(templateIds),
    {
      manual: true,
      onError: (error: any) => {
        Message.error(error.msg || t('删除用例模板失败'))
      },
    },
  )

  return [run, loading] as const
}

// 获取模板关联/未关联用例列表
export const useTemplateCaseList = () => {
  const { runAsync: run, loading } = useRequest(
    (params: GetTemplateCaseListParams) => getTemplateCaseList(params),
    {
      manual: true,
      onError: (error: any) => {
        Message.error(error.msg || t('获取用例模板关联/未关联用例列表失败'))
      },
    },
  )

  return [run, loading] as const
}

// 获取模板关联/未关联用例ids
export const useTemplateCaseIds = () => {
  const { runAsync: run, data } = useRequest(
    (params: GetTemplateCaseListParams) => getTemplateCaseIds(params),
    {
      manual: true,
      onError: (error: any) => {
        Message.error(error.msg || t('获取用例模板关联/未关联用例ids失败'))
      },
    },
  )

  return [run, data?.data?.templateResourceIds || []] as const
}

// 获取验证模式列表
export const useVerificationModeList = () => {
  const { runAsync: run, data } = useRequest(getVerificationModeList, {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || t('获取验证模式列表失败'))
    },
  })

  return [run, data?.data?.list || []] as const
}

// 获取攻击类型列表
export const useAttackTypeList = () => {
  const { runAsync: run, data } = useRequest(
    (params: { resourceIds: string[] }) => getAttackTypeList(params),
    {
      manual: true,
      onError: (error: any) => {
        Message.error(error.msg || t('获取攻击类型列表失败'))
      },
    },
  )

  return [run, data?.data?.list || []] as const
}

// 批量解绑用例模板关联的用例
export const useBatchUnbindTemplateCase = () => {
  const { runAsync: run, loading } = useRequest(
    (templateId: string, resourceIds: string[]) => batchUnbindTemplateCase(templateId, resourceIds),
    {
      manual: true,
      onError: (error: any) => {
        Message.error(error.msg || t('批量解绑用例模板关联的用例失败'))
      },
    },
  )

  return [run, loading] as const
}

// 复制模板
export const useCopyTemplate = () => {
  const { runAsync: run, loading } = useRequest(
    (templateId: string, templateName: string) => copyTemplate(templateId, templateName),
    {
      manual: true,
      onError: (error: any) => {
        Message.error(error.msg || t('复制模板失败'))
      },
    },
  )

  return [run, loading] as const
}

// 根据筛选条件获取模板关联/未关联用例ids
export const useGetTemplateCaseIdsByFilter = () => {
  const { runAsync: run, data } = useRequest(
    (params: GetTemplateCaseIdsByFilterParams) => getTemplateCaseIdsByFilter(params),
    {
      manual: true,
      onError: (error: any) => {
        Message.error(error.msg || t('根据筛选条件获取模板关联/未关联用例ids失败'))
      },
    },
  )

  return [run, data?.data?.resourceIds || []] as const
}

// 获取全部攻击类型
export const useGetAllAttackTypes = () => {
  const { runAsync: run, data } = useRequest(
    (params: {
      'templateToResourceListQuery.templateId': string
      'templateToResourceListQuery.queryType': string
      'templateToResourceListQuery.verificationModeIds': number[]
    }) => getAllAttackTypes(params),
    {
      manual: true,
      onError: (error: any) => {
        Message.error(error.msg || '获取全部攻击类型失败')
      },
    },
  )

  return [run, data?.data?.attackTypeFilters || []] as const
}
